import { BaseElement } from 'base';
import { bus } from 'bus';

const BUTTON_TAG = 'action-button';
const QUICK_ADD = `${BUTTON_TAG}.product-card__quick-add-button`;
const FACE = 'template.product-card__face';
const REGIONS = [
  '.product-card__media',
  '.product-card__labels',
  '.product-card__title-link',
  '.product-card__price'
];
const PACK = '.product-card__pack';
const FACE_KEYS = [
  'productId',
  'variantId',
  'productUrl',
  'quickAdd',
  'state',
  'analyticsProduct'
];
const SWATCH = '.product-card__colors input[type="radio"]';
const LOADING = `${BUTTON_TAG}[data-loading="true"]`;
const PHOTOS = '.product-card__photos';
const PHOTO = `${PHOTOS} > .product-card__photo`;
const CLONE = 'data-clone';
const BAR = '.product-card__scrollbar';
const DEFERRED_IMAGE_ATTRIBUTES = ['loading', 'srcset', 'src'];
const IDLE_MS = 2000;
const SETTLE_MS = 150;
const STATE_TYPES = ['preorder', 'notify'];
const MIN_BUSY_MS = 300;

class ProductCard extends BaseElement {
  #action = null;
  #detail = 0;
  #faces = new Map();
  #pending = 0;
  #photos = null;
  #bar = null;
  #count = 0;
  #index = 0;
  #expected = null;
  #touching = false;
  #ready = false;
  #frame = 0;
  #settleTimer = 0;
  #idleTimer = 0;
  #observedWidth = null;

  onConnect() {
    this.#detail = 0;
    if (this.#pending === 0) this.removeAttribute('aria-busy');
    this.#bindPhotos();
    const mode = this.dataset.quickAdd;
    const swatches = this.qa(SWATCH);
    this.#bindMode();
    if (mode === 'action' || swatches.length > 0)
      this.listen(
        this,
        'click',
        event => {
          if (event.isTrusted) this.#detail = event.detail;
        },
        true
      );
    if (mode === 'action' && this.hasAttribute('data-surface-click'))
      this.listen(this, 'click', this.#onSurface);
    if (swatches.length === 0) return;
    this.listen(this, 'change', this.#onChange);
    const group = `${this.dataset.productId}-${crypto.randomUUID()}`;
    swatches.forEach(radio => {
      radio.name = group;
    });
    this.#syncSwatches();
  }

  run(action) {
    this.#action = action;
    this.#bindMode();
    return this;
  }

  get variantId() {
    return Number(this.dataset.variantId) || null;
  }

  get productId() {
    return Number(this.dataset.productId) || null;
  }

  get url() {
    return this.dataset.productUrl || null;
  }

  #bindPhotos() {
    if (!this.hasAttribute('data-photo-scroll')) return;
    this.listen(this, 'scroll', this.#onScroll, {
      capture: true,
      passive: true
    });
    this.listen(this, 'scrollend', this.#onScrollEnd, { capture: true });
    this.listen(this, 'touchstart', this.#onTouchStart, { passive: true });
    ['touchend', 'touchcancel'].forEach(type =>
      this.listen(this, type, this.#onTouchEnd, { passive: true })
    );
    this.#observedWidth = null;
    this.observe(new ResizeObserver(entries => this.#onResize(entries)), this);
    this.cleanup(() => this.#resetPhotos());
    this.#mountPhotos();
  }

  #mountPhotos() {
    this.#resetPhotos();
    this.#photos = null;
    this.#bar = this.q(BAR);
    this.#index = 0;
    this.#expected = null;
    this.#touching = false;
    this.#ready = false;
    const photos = this.q(PHOTOS);
    if (!photos) return;
    const slides = this.qa(`${PHOTO}:not([${CLONE}])`);
    this.#count = slides.length;
    if (this.#count < 2) return;
    if (!this.q(`${PHOTO}[${CLONE}]`)) {
      photos.prepend(this.#copy(slides.at(-1)));
      photos.append(this.#copy(slides[0]));
    }
    this.#photos = photos;
  }

  #onResize(entries) {
    const width = entries.at(-1)?.contentRect.width ?? 0;
    if (width === this.#observedWidth) return;
    this.#observedWidth = width;
    this.#jump(this.#index);
  }

  #resetPhotos() {
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#idleTimer);
    this.#bar?.removeAttribute('data-active');
  }

  #copy(slide) {
    const copy = document.createElement('span');
    copy.className = slide.className;
    copy.setAttribute(CLONE, '');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    for (const picture of slide.children) {
      const source = picture.firstElementChild;
      if (!source) continue;
      const image = document.createElement('img');
      image.loading = 'lazy';
      for (const { name, value } of source.attributes)
        if (!DEFERRED_IMAGE_ATTRIBUTES.includes(name))
          image.setAttribute(name, value);
      ['srcset', 'src'].forEach(name => {
        const value = source.getAttribute(name);
        if (value !== null) image.setAttribute(name, value);
      });
      const frame = document.createElement('picture');
      frame.append(image);
      copy.append(frame);
    }
    return copy;
  }

  #onScroll(event) {
    if (!this.#ready || event.target !== this.#photos) return;
    const position = this.#photos.scrollLeft;
    if (this.#expected !== null && Math.abs(position - this.#expected) < 1)
      return;
    this.#expected = null;
    this.#wake();
    this.#prime(Math.round(this.#slot()));
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#paint(this.#slot() - 1));
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#onSettle(), SETTLE_MS);
  }

  #onScrollEnd(event) {
    if (event.target === this.#photos) this.#onSettle();
  }

  #onTouchStart(event) {
    if (this.#photos?.contains(event.target)) this.#touching = true;
  }

  #onTouchEnd(event) {
    if (!this.#photos?.contains(event.target)) return;
    this.#touching = false;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#onSettle(), SETTLE_MS);
  }

  #onSettle() {
    clearTimeout(this.#settleTimer);
    if (!this.#photos || this.#touching || !this.#width()) return;
    const slot = Math.round(this.#slot());
    const index = (((slot - 1) % this.#count) + this.#count) % this.#count;
    if (slot <= 0 || slot > this.#count) {
      this.#jump(index);
      return;
    }
    this.#index = index;
    this.#paint(index);
  }

  #jump(index) {
    if (!this.#photos) return;
    const width = this.#width();
    if (!width) return;
    const direction =
      getComputedStyle(this.#photos).direction === 'rtl' ? -1 : 1;
    this.#index = index;
    this.#expected = (index + 1) * width * direction;
    this.#photos.scrollLeft = this.#expected;
    this.#paint(index);
    this.#ready = true;
  }

  #width() {
    return this.#photos.getBoundingClientRect().width;
  }

  #slot() {
    return Math.abs(this.#photos.scrollLeft) / this.#width();
  }

  #prime(slot) {
    [slot - 1, slot + 1].forEach(position => {
      for (const picture of this.#photos.children[position]?.children ?? []) {
        const image = picture.firstElementChild;
        if (image?.loading === 'lazy') image.loading = 'eager';
      }
    });
  }

  #paint(position) {
    const clamped = Math.min(Math.max(position, 0), this.#count - 1);
    this.#bar?.style.setProperty('--product-card-scroll', String(clamped));
  }

  #wake() {
    if (!this.#bar) return;
    this.#bar.setAttribute('data-active', '');
    clearTimeout(this.#idleTimer);
    this.#idleTimer = setTimeout(
      () => this.#bar?.removeAttribute('data-active'),
      IDLE_MS
    );
  }

  #onSurface(event) {
    if (!this.#action || !event.isTrusted || event.defaultPrevented) return;
    const control = this.q(QUICK_ADD)?.control;
    if (!control || control.contains(event.target)) return;
    control.click();
  }

  #bindMode() {
    const mode = this.dataset.quickAdd;
    if (mode === 'add') this.#bindQuickAdd(() => this.#request('add'));
    if (mode === 'action' && this.#action)
      this.#bindQuickAdd(() => this.#request('options'));
  }

  #bindQuickAdd(handler) {
    customElements.whenDefined(BUTTON_TAG).then(() => {
      if (!this.isConnected) return;
      this.q(QUICK_ADD)?.run(handler);
    });
  }

  #onChange(event) {
    if (!event.target.matches(SWATCH)) return;
    if (
      this.getAttribute('aria-busy') === 'true' ||
      this.q(LOADING) ||
      !this.#swap(event.target.value)
    )
      this.#syncSwatches();
  }

  #swap(id) {
    const current = this.dataset.productId;
    if (id === current) return true;
    const regions = this.#regions();
    const live = regions.map(selector => this.q(selector));
    const missing = regions.find((_, index) => !live[index]);
    if (missing) {
      console.error('[product-card] missing region:', missing);
      return false;
    }
    const next = this.#faces.get(id) ?? this.#fromTemplate(id, regions);
    if (!next) {
      console.error('[product-card] missing face:', id);
      return false;
    }
    this.#faces.set(current, { nodes: live, data: this.#pick(this.dataset) });
    this.#faces.delete(id);
    live.forEach((node, index) => node.replaceWith(next.nodes[index]));
    FACE_KEYS.forEach(key => {
      if (next.data[key] === undefined) delete this.dataset[key];
      else this.dataset[key] = next.data[key];
    });
    this.#mountPhotos();
    this.#jump(0);
    this.#bindMode();
    return true;
  }

  #regions() {
    return this.q(PACK) ? [...REGIONS, PACK] : REGIONS;
  }

  #fromTemplate(id, regions) {
    const template = this.qa(FACE).find(face => face.dataset.productId === id);
    if (!template) return null;
    const children = [...template.content.children];
    const nodes = regions.map(selector =>
      children.find(node => node.matches(selector))?.cloneNode(true)
    );
    if (nodes.some(node => !node)) return null;
    return { nodes, data: this.#pick(template.dataset) };
  }

  #pick(dataset) {
    return Object.fromEntries(FACE_KEYS.map(key => [key, dataset[key]]));
  }

  #syncSwatches() {
    this.qa(SWATCH).forEach(radio => {
      radio.checked = radio.value === this.dataset.productId;
    });
  }

  #request(mode) {
    const state = this.dataset.state;
    const type = STATE_TYPES.includes(state) ? state : mode;
    if (type !== 'add')
      this.q(QUICK_ADD)?.control?.focus(
        this.#detail === 0
          ? { preventScroll: true }
          : { preventScroll: true, focusVisible: false }
      );
    return this.#send({
      type,
      variantId: this.variantId,
      productId: this.productId,
      url: this.url
    });
  }

  async #send(request) {
    if (!this.#action) return;
    this.#pending += 1;
    this.setAttribute('aria-busy', 'true');
    const started = performance.now();
    try {
      await this.#action(request);
    } catch (error) {
      console.error('[product-card] quick add failed:', error);
      if (this.dataset.error)
        bus.publish('toast:raised', {
          id: crypto.randomUUID(),
          text: this.dataset.error
        });
    } finally {
      const rest = MIN_BUSY_MS - (performance.now() - started);
      if (rest > 0) await new Promise(resolve => setTimeout(resolve, rest));
      this.#pending -= 1;
      if (this.#pending === 0 && this.isConnected)
        this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
